import { Box, Tooltip, Typography } from '@mui/material';

export interface StudentServiceComparisonRow {
    name: string;
    participationRate: number;
    averageHours: number;
}

interface Props {
    rows: StudentServiceComparisonRow[];
    subtitle: string;
}

const PARTICIPATION_COLOR = '#6b1ce6';
const HOURS_COLOR = '#1c6be6';

export const StudentServiceComparisonChart = ({ rows, subtitle }: Props) => {
    const maxParticipationRate = Math.max(...rows.map(row => row.participationRate), 1);
    const maxAverageHours = Math.max(...rows.map(row => row.averageHours), 1);
    const sortedRows = [...rows].sort((a, b) =>
        b.participationRate - a.participationRate
        || b.averageHours - a.averageHours
        || a.name.localeCompare(b.name));

    return (
        <Box sx={{ height: '100%', overflow: 'auto', px: { xs: 1, md: 2 }, pb: 2 }}>
            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mb: 1 }}>
                {subtitle}
            </Typography>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(100px, 1fr) minmax(145px, .72fr) minmax(100px, 1fr)',
                    columnGap: { xs: .75, md: 1 },
                    alignItems: 'end',
                    minWidth: 400,
                    mb: 1,
                }}
            >
                <Typography align="right" sx={{ color: PARTICIPATION_COLOR, fontSize: { xs: '.75rem', md: '.9rem' }, fontWeight: 700, lineHeight: 1.2 }}>
                    Participation Rates
                </Typography>
                <Box />
                <Typography align="left" sx={{ color: HOURS_COLOR, fontSize: { xs: '.75rem', md: '.9rem' }, fontWeight: 700, lineHeight: 1.2 }}>
                    Average Hours
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: .75, minWidth: 400 }}>
                {sortedRows.map(row => (
                    <Box
                        key={row.name}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(100px, 1fr) minmax(145px, .72fr) minmax(100px, 1fr)',
                            columnGap: { xs: .75, md: 1 },
                            alignItems: 'center',
                            minHeight: 30,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 0 }}>
                            <Box sx={{ width: '100%', height: 24, display: 'flex', justifyContent: 'flex-end' }}>
                                <Tooltip title={`${row.participationRate.toFixed(1)}%`} arrow placement="top">
                                    <Box
                                        role="img"
                                        tabIndex={0}
                                        aria-label={`${row.name}: ${row.participationRate.toFixed(1)}% participation rate`}
                                        sx={{
                                            width: `${row.participationRate / maxParticipationRate * 100}%`,
                                            minWidth: row.participationRate > 0 ? 2 : 0,
                                            bgcolor: PARTICIPATION_COLOR,
                                            cursor: 'pointer',
                                            transition: 'filter 150ms ease, transform 150ms ease, box-shadow 150ms ease',
                                            transformOrigin: 'right center',
                                            '&:hover, &:focus-visible': {
                                                filter: 'brightness(1.18)',
                                                transform: 'scaleY(1.12)',
                                                boxShadow: '0 2px 7px rgba(0, 0, 0, .28)',
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                        <Typography align="center" sx={{ fontSize: '.75rem', lineHeight: 1.1 }}>
                            {row.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <Box sx={{ width: '100%', height: 24 }}>
                                <Tooltip title={`${row.averageHours.toFixed(1)} hours`} arrow placement="top">
                                    <Box
                                        role="img"
                                        tabIndex={0}
                                        aria-label={`${row.name}: ${row.averageHours.toFixed(1)} average hours`}
                                        sx={{
                                            width: `${row.averageHours / maxAverageHours * 100}%`,
                                            minWidth: row.averageHours > 0 ? 2 : 0,
                                            height: '100%',
                                            bgcolor: HOURS_COLOR,
                                            cursor: 'pointer',
                                            transition: 'filter 150ms ease, transform 150ms ease, box-shadow 150ms ease',
                                            transformOrigin: 'left center',
                                            '&:hover, &:focus-visible': {
                                                filter: 'brightness(1.18)',
                                                transform: 'scaleY(1.12)',
                                                boxShadow: '0 2px 7px rgba(0, 0, 0, .28)',
                                                outline: 'none',
                                            },
                                        }}
                                    />
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};
